/**
 * ChoiceList — choices drawn as square buttons, each with its description;
 * never a native dropdown. Over ten options, a find box filters them.
 *
 * Used by the item page's editors, the item's versions panel and the plan's
 * state bar. Moved here from the Item Detail third-pass view
 * (features/item-detail-third-pass/ItemDetailThirdPassView.tsx), which
 * re-exports it, so product components outside the item page no longer borrow
 * from that view (Proposal 09 § 4, coupling 1). Markup and behaviour unchanged.
 */
import { useState } from 'react';
import { TextInput } from '@/components/ds';
import { btnSecondary } from '@/components/mono/primitives';

export interface ChoiceOption {
  value: string;
  label: string;
  description?: string;
}

/** Choices drawn as square buttons, each with its description — never a native dropdown. */
export function ChoiceList({
  options,
  value,
  onPick,
  label,
  disabled,
  compact,
  stacked,
}: {
  options: ChoiceOption[];
  value: string;
  onPick: (value: string) => void;
  label: string;
  disabled?: boolean;
  /** Short labels (1–5) sit in one row. */
  compact?: boolean;
  /** One column, for narrow places like the right-hand panel. */
  stacked?: boolean;
}) {
  const [query, setQuery] = useState('');
  const searchable = options.length > 10;
  const q = query.trim().toLowerCase();
  const shown = q ? options.filter((o) => `${o.label} ${o.description ?? ''}`.toLowerCase().includes(q)) : options;
  return (
    <div role="radiogroup" aria-label={label}>
      {searchable && (
        <div className="mb-2 max-w-sm">
          <TextInput aria-label={`Find ${label.toLowerCase()}`} value={query} placeholder={`Find ${label.toLowerCase()}…`} autoFocus onChange={(e) => setQuery(e.target.value)} />
        </div>
      )}
      <div className={compact ? 'flex flex-wrap gap-2' : stacked ? 'grid grid-cols-1 gap-1.5' : 'grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3'}>
        {shown.map((o) => {
          const on = o.value === value;
          return (
            <button
              key={o.value}
              role="radio"
              aria-checked={on}
              disabled={disabled}
              {...btnSecondary(`!h-auto ${compact ? 'min-w-[3rem]' : '!justify-start text-left'} flex-col !items-start gap-0.5 py-2 font-sans text-sm normal-case tracking-normal`)}
              style={{
                borderColor: on ? 'var(--slate)' : 'var(--rule-bright)',
                background: on ? 'var(--slate-dim)' : undefined,
                color: on ? 'var(--text-pri)' : 'var(--text-sec)',
                boxShadow: on ? 'inset 3px 0 0 var(--slate)' : undefined,
              }}
              onClick={() => onPick(o.value)}
            >
              <span className={on ? 'font-semibold' : undefined}>{o.label}</span>
              {o.description && <span className="whitespace-normal text-[12.5px] leading-snug text-[var(--text-dim)]">{o.description}</span>}
            </button>
          );
        })}
        {shown.length === 0 && <span className="text-sm text-[var(--text-dim)]">Nothing matches.</span>}
      </div>
    </div>
  );
}
